import { Component, computed, inject } from '@angular/core';
import { LucideModule } from '../../../../../shared/modules/lucide.module';
import { OuvidoriaPublicaService } from '../../services/ouvidoria-publica.service';
import { ManifestacaoStore } from '../manifestacao.store';

type CorEtapa = 'escuro' | 'laranja' | 'ambar' | 'roxo' | 'verde';

interface LinhaPrazo {
  rotulo: string;
  valor: string;
  complemento?: string;
}

interface EtapaPrazo {
  id: string;
  ordem: number;
  titulo: string;
  cor: CorEtapa;
  linhas?: readonly LinhaPrazo[];
  selo?: string;
  destaque?: boolean;
}

const ETAPAS: readonly EtapaPrazo[] = [
  {
    id: 'registro',
    ordem: 1,
    titulo: 'Registro da Manifestação',
    cor: 'escuro',
    destaque: true,
  },
  {
    id: 'admissibilidade',
    ordem: 2,
    titulo: 'Análise de Admissibilidade',
    cor: 'laranja',
    linhas: [
      { rotulo: 'Dados insuficientes →', valor: 'Complementação (30 dias)' },
      { rotulo: 'Se não responder:', valor: 'Arquivamento' },
    ],
  },
  {
    id: 'area-tecnica',
    ordem: 3,
    titulo: 'Envio para a Área Técnica',
    cor: 'ambar',
    linhas: [
      { rotulo: 'Prazo:', valor: 'Até 20 dias' },
      { rotulo: 'Prorrogação:', valor: '+20 dias' },
    ],
  },
  {
    id: 'resposta-final',
    ordem: 4,
    titulo: 'Elaboração da Resposta Final',
    cor: 'roxo',
    // O prazo desta etapa é o do CANAL do órgão, e entra em `etapas` — as demais linhas do
    // fluxo continuam ilustrativas.
    linhas: [{ rotulo: 'Prorrogação:', valor: 'Uma única vez, mediante justificativa' }],
  },
  {
    id: 'envio-resposta',
    ordem: 5,
    titulo: 'Envio da Resposta ao Cidadão',
    cor: 'verde',
  },
];

@Component({
  selector: 'app-etapa-prazo-ouvidoria',
  templateUrl: './etapa-prazo-ouvidoria.component.html',
  styleUrls: ['./etapa-prazo-ouvidoria.component.css'],
  imports: [LucideModule],
})
export class EtapaPrazoOuvidoriaComponent {
  private readonly ouvidoria = inject(OuvidoriaPublicaService);

  protected readonly store = inject(ManifestacaoStore);

  /**
   * O fluxo desenhado, com o prazo da resposta final e o selo de prazo máximo vindos do canal do
   * órgão — não das constantes da Lei 13.460. É este prazo que vale para esta manifestação, e é
   * ele que o comprovante vai repetir.
   */
  readonly etapas = computed<readonly EtapaPrazo[]>(() => {
    const prazo = this.ouvidoria.prazoTexto();

    if (!prazo) return ETAPAS;

    return ETAPAS.map((etapa) => {
      if (etapa.id === 'resposta-final') {
        return {
          ...etapa,
          linhas: [{ rotulo: 'Prazo:', valor: `Até ${prazo}` }, ...(etapa.linhas ?? [])],
        };
      }

      if (etapa.id === 'envio-resposta') {
        return { ...etapa, selo: `Prazo da Ouvidoria: ${prazo}` };
      }

      return etapa;
    });
  });

  /**
   * Limite de tentativas, indisponibilidade e recusa do desafio de formulário acontecem NO ENVIO
   * e não têm campo a corrigir: o cidadão fica onde está e a mensagem aparece aqui, ao lado do
   * botão que ele vai clicar de novo.
   */
  readonly mensagemEnvio = computed(() => {
    const erro = this.store.erroEnvio();

    return erro && (erro.tipo === 'limite' || erro.tipo === 'indisponivel' || erro.tipo === 'desafio')
      ? erro.mensagem
      : '';
  });
}
